import { formatUnits } from 'viem'
import { config } from '../appConfig'
import ChainFusionClient from '../clients/ChainFusionClient'
import HyperLiquidClient from '../clients/HyperLiquidClient'
import TatumClient from '../clients/TatumClient'
import {
  sendBTC,
  sendSol as sendSolana,
  sendSpl,
  sendUTXO,
  transferErc20,
  transferEth,
  transferToken,
} from '../handlers/walletActions/wallet'
import { Chain, EvmToken, PlugToken, SolanaToken } from '../interfaces/common'
import { UTXOChain } from '../interfaces/walletActions'
import { Wallet } from '../providers/WalletProvider'
import { asEvmToken, asSolanaToken } from './castToken'
import { GasCalculator } from './GasCalculator'
import { getEthProvider } from './getEthProvider'

export const sendToken = async (token: PlugToken, to: string, amount: bigint, wallet: Wallet) => {
  if (token.evmChain) {
    return sendEth(asEvmToken(token), to, amount, wallet)
  } else if (token.chain === Chain.BTC) {
    return sendBtc(to, amount, wallet)
  } else if (token.chain === Chain.DOGE) {
    return sendUtxoChain(Chain.DOGE, to, amount, wallet)
  } else if (token.chain === Chain.LTC) {
    return sendUtxoChain(Chain.LTC, to, amount, wallet)
  } else if (token.chain === Chain.SOL) {
    return sendSol(asSolanaToken(token), to, amount, wallet)
  } else if (token.chain === Chain.HYPERCORE) {
    return sendHypercore(token, to, amount, wallet)
  } else if (token) {
    return transferToken({
      receiver: { address: to },
      amount: amount.toString(),
      tokenCanisterId: token.address,
      standard: token.standard,
      tokenId: token.id,
      chain: token.chain,
    })
  }
}

const sendEth = async (token: EvmToken, to: string, amount: bigint, wallet: Wallet) => {
  if (!wallet.ethAddress) {
    throw new Error('Missing wallet ETH address')
  }

  const gasCalculator = new GasCalculator(token.evmChain)
  const gas = await gasCalculator.getEthGas(wallet.ethAddress, amount)

  const nonce = await getEthProvider(token.evmChain.rpcEndpoint).getTransactionCount({
    address: wallet.ethAddress as `0x${string}`,
  })

  if (!gas?.maxFeePerGas || gas.maxPriorityFeePerGas === undefined)
    throw new Error('No gas fee available')

  if (token.standard === 'ERC20') {
    const erc20GasLimit = await gasCalculator.estimateErc20TransferGas(
      wallet.ethAddress,
      token.address!,
      Number(amount)
    )

    if (!erc20GasLimit) {
      throw new Error('No ERC-20 gas fee available')
    }

    await transferErc20({
      receiver: to,
      amount: amount.toString(),
      gasLimit: erc20GasLimit.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      contractAddress: token.address!,
      nonce,
      chain: token.evmChain,
    })
  } else {
    await transferEth({
      address: to,
      amount: amount.toString(),
      gasLimit: gas.gasLimit?.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce,
      chain: token.evmChain,
    })
  }
}

const sendBtc = async (to: string, amount: bigint, wallet: Wallet) => {
  if (!wallet.btcAddress) {
    throw new Error('Missing wallet BTC address')
  }

  const utxos = await TatumClient.getUTXOs(wallet.btcAddress, Chain.BTC)
  const fee = await new GasCalculator().estimateUTXOTransferFee(
    wallet.btcAddress,
    amount,
    Chain.BTC
  )

  await sendBTC({ to, utxos, amount: amount.toString(), fee: fee.toString() })
}

const sendUtxoChain = async (chain: UTXOChain, to: string, amount: bigint, wallet: Wallet) => {
  const addressKey = chain === Chain.DOGE ? 'dogeAddress' : 'ltcAddress'
  const address = wallet[addressKey as keyof Wallet] as string | undefined

  if (!address) {
    throw new Error(`Missing wallet ${chain.toUpperCase()} address`)
  }

  const utxos = await TatumClient.getUTXOs(address, chain)
  const fee = await new GasCalculator().estimateUTXOTransferFee(address, amount, chain)

  await sendUTXO({ chain, to, utxos, amount: amount.toString(), fee: fee.toString() })
}

// HyperCore (HyperLiquid L1) transfers are not on-chain transactions: they are
// signed exchange actions. `hypercore_account` selects the action — "perp" funds
// move with `usdSend`, "spot" tokens with `spotSend` (which needs the token's
// `NAME:tokenId` identifier, stored in `token.address`).
const sendHypercore = async (token: PlugToken, to: string, amount: bigint, wallet: Wallet) => {
  if (!wallet.ethAddress) {
    throw new Error('Missing wallet address for HyperCore')
  }

  const humanAmount = formatUnits(amount, token.decimals)
  const hyperliquidClient = new HyperLiquidClient()
  const chainFusionClient = new ChainFusionClient(
    config.getAgent({ principalId: wallet.principalId })
  )

  if (token.additionalData.hypercore_account === 'perp') {
    const { action, digest, nonce } = hyperliquidClient.prepareUsdSend(humanAmount, to)
    const signature = await chainFusionClient.signEthHash(digest)

    if (!signature) throw new Error('Unable to sign HyperCore transfer')

    return hyperliquidClient.executeExchangeAction({ action, signature, nonce })
  }

  if (!token.address) {
    throw new Error('Missing HyperCore token identifier')
  }

  const { action, digest, nonce } = hyperliquidClient.prepareSpotSend(
    humanAmount,
    to,
    token.address
  )
  const signature = await chainFusionClient.signEthHash(digest)

  if (!signature) throw new Error('Unable to sign HyperCore transfer')

  return hyperliquidClient.executeExchangeAction({ action, signature, nonce })
}

const sendSol = async (token: SolanaToken, to: string, amount: bigint, wallet: Wallet) => {
  if (!wallet.solAddress) {
    throw new Error('Missing wallet SOL address')
  }

  const params = { to, from: wallet.solAddress, amount: amount.toString() }

  if (token.standard === 'SPL') {
    const { address, decimals, additionalData } = token

    return sendSpl({ ...params, token: { address, decimals, additionalData } })
  } else {
    return sendSolana(params)
  }
}
